import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Lock, Sparkles, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

/**
 * Global upsell modal that pops up whenever a Demo user (or the backend
 * returning 403 detail:"demo_locked") tries to use a premium feature.
 *
 * Two ways to open it:
 *  - imperatively:  window.dispatchEvent(new CustomEvent("demo-locked", { detail: {feature: "Analizza Link"} }))
 *  - the axios interceptor does this automatically on 403 + detail=demo_locked.
 */
const LockedFeatureDialog = () => {
  const [open, setOpen] = useState(false);
  const [feature, setFeature] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const onLocked = (e) => {
      setFeature(e?.detail?.feature || "");
      setOpen(true);
    };
    window.addEventListener("demo-locked", onLocked);
    return () => window.removeEventListener("demo-locked", onLocked);
  }, []);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        data-testid="demo-locked-dialog"
        className="max-w-md rounded-3xl border-0 bg-gradient-to-br from-white via-white to-[#F5F6FA] shadow-2xl p-0 overflow-hidden"
      >
        {/* Hero band */}
        <div className="relative px-6 pt-7 pb-5 bg-gradient-to-br from-[#12466F] via-[#0F5A89] to-[#0A84FF] text-white">
          <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-white/10 blur-2xl" />
          <div className="absolute -bottom-10 -left-6 w-32 h-32 rounded-full bg-[#FFD60A]/25 blur-2xl" />
          <div className="relative flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center shadow-inner">
              <Lock className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/70 font-semibold">Modalità Demo</div>
              <div className="font-display font-black text-xl leading-tight mt-0.5">Funzione riservata</div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {feature && (
            <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500 font-semibold bg-slate-100 rounded-full px-2.5 py-1 mb-3">
              <Sparkles className="w-3 h-3" /> {feature}
            </div>
          )}
          <p className="text-slate-700 text-[15px] leading-relaxed">
            Questa funzione è riservata agli utenti abbonati.{" "}
            <span className="font-semibold text-slate-900">Scopri i nostri piani</span> per sbloccare
            l'importazione da URL, l'analisi degli screenshot, la creazione degli immobili e molto altro.
          </p>

          <ul className="mt-4 space-y-1.5 text-[13px] text-slate-600">
            <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Analizza Link (Immobiliare.it, Idealista…)</li>
            <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Analisi Screenshot con AI</li>
            <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Importazione Excel / CSV</li>
            <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Schede e immobili illimitati</li>
          </ul>
        </div>

        <DialogFooter className="px-6 pb-6 pt-2 flex-row gap-2 sm:justify-between">
          <Button
            variant="ghost"
            className="rounded-full text-slate-500 hover:text-slate-800 hover:bg-slate-100"
            onClick={() => setOpen(false)}
            data-testid="demo-locked-dismiss"
          >
            Continua in Demo
          </Button>
          <Button
            className="rounded-full bg-gradient-to-r from-[#0A84FF] to-[#0060df] hover:brightness-110 text-white shadow-lg shadow-[#0A84FF]/30"
            onClick={() => { setOpen(false); navigate("/pricing"); }}
            data-testid="demo-locked-upgrade"
          >
            Scopri i nostri piani <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LockedFeatureDialog;
